import React, { useState, useEffect } from 'react';
import { errorTracker, ErrorLog } from '../services/errorTracker';
import { Download, Trash2, Filter, Search, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';

interface ErrorLogViewerProps {
  onClose?: () => void;
}

const ErrorLogViewer: React.FC<ErrorLogViewerProps> = ({ onClose }) => {
  const [logs, setLogs] = useState<ErrorLog[]>([]);
  const [filter, setFilter] = useState<{
    source?: string;
    component?: string;
    level?: string;
    search?: string;
  }>({});
  const [expandedLog, setExpandedLog] = useState<string | null>(null);

  useEffect(() => {
    loadLogs();
  }, [filter]);

  const loadLogs = () => {
    let filteredLogs = errorTracker.getLogs();
    
    if (filter.source) {
      filteredLogs = filteredLogs.filter(log => log.source === filter.source);
    }
    if (filter.component) {
      filteredLogs = filteredLogs.filter(log => log.component === filter.component);
    }
    if (filter.level) {
      filteredLogs = filteredLogs.filter(log => log.level === filter.level);
    }
    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      filteredLogs = filteredLogs.filter(log => 
        log.error.message.toLowerCase().includes(searchLower) ||
        log.component.toLowerCase().includes(searchLower) ||
        log.action.toLowerCase().includes(searchLower)
      );
    }
    
    setLogs(filteredLogs);
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
      case 'info':
        return <Info className="w-4 h-4 text-blue-400" />;
      default:
        return <Info className="w-4 h-4 text-slate-400" />;
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error':
        return 'bg-red-500/10 border-red-500/20 text-red-400';
      case 'warning':
        return 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400';
      case 'info':
        return 'bg-blue-500/10 border-blue-500/20 text-blue-400';
      default:
        return 'bg-slate-500/10 border-slate-500/20 text-slate-400';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div>
            <h2 className="text-2xl font-bold text-white">Error Logs</h2>
            <p className="text-slate-400 text-sm mt-1">
              {logs.length} log{logs.length !== 1 ? 's' : ''} found
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => errorTracker.downloadLogs()}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={() => {
                if (confirm('Clear all logs?')) {
                  errorTracker.clearLogs();
                  loadLogs();
                }
              }}
              className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Clear
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-800 rounded-lg"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-slate-700 bg-slate-800/50">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search logs..."
                value={filter.search || ''}
                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm"
              />
            </div>
            <select
              value={filter.source || ''}
              onChange={(e) => setFilter({ ...filter, source: e.target.value || undefined })}
              className="px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm"
            >
              <option value="">All Sources</option>
              <option value="frontend">Frontend</option>
              <option value="backend">Backend</option>
              <option value="api">API</option>
            </select>
            <select
              value={filter.level || ''}
              onChange={(e) => setFilter({ ...filter, level: e.target.value || undefined })}
              className="px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm"
            >
              <option value="">All Levels</option>
              <option value="error">Error</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
            <input
              type="text"
              placeholder="Component..."
              value={filter.component || ''}
              onChange={(e) => setFilter({ ...filter, component: e.target.value || undefined })}
              className="px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm"
            />
          </div>
        </div>

        {/* Logs List */}
        <div className="flex-1 overflow-y-auto p-4">
          {logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Info className="w-12 h-12 mx-auto mb-4 text-slate-600" />
              <p>No logs found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    expandedLog === log.id
                      ? 'bg-slate-800 border-indigo-500'
                      : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                  }`}
                  onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 p-2 rounded ${getLevelColor(log.level)}`}>
                      {getLevelIcon(log.level)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-slate-400 uppercase">
                            {log.source}
                          </span>
                          <span className="text-slate-600">•</span>
                          <span className="text-sm font-semibold text-white">
                            {log.component}
                          </span>
                          <span className="text-slate-600">•</span>
                          <span className="text-sm text-slate-300">
                            {log.action}
                          </span>
                        </div>
                        <span className="text-xs text-slate-500">
                          {formatTimestamp(log.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-300 mb-2">
                        {log.error.message}
                      </p>
                      {expandedLog === log.id && (
                        <div className="mt-3 pt-3 border-t border-slate-700 space-y-2">
                          {log.error.stack && (
                            <div>
                              <p className="text-xs font-semibold text-slate-400 mb-1">Stack Trace:</p>
                              <pre className="text-xs text-slate-400 bg-slate-900 p-3 rounded overflow-x-auto">
                                {log.error.stack}
                              </pre>
                            </div>
                          )}
                          {Object.keys(log.context).length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-slate-400 mb-1">Context:</p>
                              <pre className="text-xs text-slate-400 bg-slate-900 p-3 rounded overflow-x-auto">
                                {JSON.stringify(log.context, null, 2)}
                              </pre>
                            </div>
                          )}
                          {log.userInfo && (
                            <div>
                              <p className="text-xs font-semibold text-slate-400 mb-1">User Info:</p>
                              <pre className="text-xs text-slate-400 bg-slate-900 p-3 rounded">
                                {JSON.stringify(log.userInfo, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorLogViewer;

